import {
  PageChangeEvent,
  RenderPage,
  RenderPageProps,
} from "@react-pdf-viewer/core";
import {
  HighlightArea,
  RenderHighlightsProps,
  Trigger,
  highlightPlugin,
} from "@react-pdf-viewer/highlight";
import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import apiClient, { basePath } from "../../../utils/api";
import { PDFViewer } from "../../Shared/PDFViewer/PDFViewer";

import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import { Box, Button, CircularProgress, Typography } from "@mui/material";
import "@react-pdf-viewer/highlight/lib/styles/index.css";
import {} from "../../../services";
import "./AnnotateDocument.css";
import ClickToSelect from "./ClickToSelect";

interface Chunk {
  text: string;
  type: string;
  metadata: any;
  element_id: string;
}

interface Document {
  id: string;
  fileName: string;
  chunks: Chunk[];
  documentContent: string;
}

interface Coordinates {
  points: [number, number][];
  system: string;
  layout_width: number;
  layout_height: number;
}

const convertToHighlightArea = (
  data: Coordinates,
  pageIndex: number = 0
): HighlightArea => {
  const { points, layout_width, layout_height } = data;

  // Extract coordinates
  const xCoords = points.map((point) => point[0]);
  const yCoords = points.map((point) => point[1]);

  // Calculate boundaries
  const left = Math.min(...xCoords);
  const right = Math.max(...xCoords);
  const top = Math.min(...yCoords);
  const bottom = Math.max(...yCoords);

  // Calculate dimensions in pixels
  const widthPx = right - left;
  const heightPx = bottom - top;

  // Convert dimensions to percentages
  const leftPercent = (left / layout_width) * 100;
  const topPercent = (top / layout_height) * 100;
  const widthPercent = (widthPx / layout_width) * 100;
  const heightPercent = (heightPx / layout_height) * 100;

  return {
    height: heightPercent,
    left: leftPercent,
    pageIndex: pageIndex - 1,
    top: topPercent,
    width: widthPercent,
  };
};

interface HighlightItem {
  id: number;
  image: string;
  selectionRegion: HighlightArea;
}

const selectionToChunk = (
  input: HighlightItem,
  fileName: string,
  fileType: string
): Chunk => {
  const { id, image, selectionRegion } = input;

  // Convert percentage dimensions back to pixels
  const layoutWidth = 1700; // Example value, should be provided or derived from context
  const layoutHeight = 2200; // Example value, should be provided or derived from context

  const leftPx = (selectionRegion.left / 100) * layoutWidth;
  const topPx = (selectionRegion.top / 100) * layoutHeight;
  const widthPx = (selectionRegion.width / 100) * layoutWidth;
  const heightPx = (selectionRegion.height / 100) * layoutHeight;

  // Calculate the points
  const points: [number, number][] = [
    [leftPx, topPx],
    [leftPx, topPx + heightPx],
    [leftPx + widthPx, topPx + heightPx],
    [leftPx + widthPx, topPx],
  ];

  const languages = ["eng"];
  const image_mime_type = "image/jpeg";

  const imageBase64 = image.split(",")[1];

  return {
    text: "text...",
    type: "Image",
    metadata: {
      filename: fileName,
      filetype: fileType,
      languages,
      coordinates: {
        points,
        system: "PixelSpace",
        layout_width: layoutWidth,
        layout_height: layoutHeight,
      },
      page_number: selectionRegion.pageIndex + 1,
      image_base64: imageBase64,
      image_mime_type,
    },
    element_id: `${id}`,
  };
};

export const AnnotateDocument: React.FC = () => {
  const documentID = useLocation().pathname.split("/").pop() || "";
  // three buttons
  const [loadingChunk, setLoadingChunk] = useState<boolean>(false);
  const [loadingChunkStatus, setLoadingChunkStatus] = useState<boolean>(false);
  const [chunkMessage, setChunkMessage] = useState<string | null>(null);
  const [chunkStatus, setChunkStatus] = useState<string | null>(null);
  const [chunkJobId, setChunkJobId] = useState<string | null>(null);
  const [loadingDescribe, setLoadingDescribe] = useState<boolean>(false);
  const [describeSuccess, setDescribeSuccess] = useState<boolean | null>(null);
  const [loadingEmbed, setLoadingEmbed] = useState<boolean>(false);
  const [embedSuccess, setEmbedSuccess] = useState<boolean | null>(null);
  const [loadingEmbedRemove, setLoadingEmbedRemove] = useState<boolean>(false);
  const [embedRemoveSuccess, setEmbedRemoveSuccess] = useState<boolean | null>(
    null
  );

  const handleDocumentChunk = async () => {
    try {
      setLoadingChunk(true);
      const res = await apiClient.documentControllerChunk({
        chunkRequest: { id: documentID },
      });
      if (res) {
        console.log(res);
        setChunkJobId(res.jobId);
        setChunkMessage(res.message);
        setLoadingChunk(false);
      }
      console.log("chunk id", chunkJobId);
    } catch (error) {
      setLoadingChunk(false);
      console.log(error);
    }
  };

  const checkChunkStatus = async () => {
    setLoadingChunkStatus(true);
    if (chunkJobId) {
      try {
        const res = await apiClient.documentControllerChunkStatus({
          chunkQueueRequest: { jobId: chunkJobId },
        });
        if (res) {
          console.log(res);
          setChunkStatus(res.message);
          setLoadingChunkStatus(false);
        }
      } catch (error) {
        console.log(error);
        setLoadingChunkStatus(false);
      }
    } else {
      setChunkStatus("No chunk Job Id found");
      setLoadingChunkStatus(false);
    }
  };

  const handleDocumentDesribe = async () => {
    try {
      setLoadingDescribe(true);
      const res = await apiClient.documentControllerDescribe({
        chunkRequest: { id: documentID },
      });
      if (res) {
        setDescribeSuccess(true);
        setLoadingDescribe(false);
      }
    } catch (error) {
      setDescribeSuccess(false);
      setLoadingDescribe(false);
      console.log(error);
    }
  };

  const handleDocumentEmbed = async () => {
    try {
      setLoadingEmbed(true);
      const res = await apiClient.documentControllerEmbed({
        chunkRequest: { id: documentID },
      });
      if (res) {
        setEmbedSuccess(true);
        setLoadingEmbed(false);
      }
    } catch (error) {
      setEmbedSuccess(false);
      setLoadingEmbed(false);
      console.log(error);
    }
  };

  const handleDocumentEmbedRemove = async () => {
    try {
      setLoadingEmbedRemove(true);
      const res = await apiClient.documentControllerRemoveEmbed({
        chunkRequest: { id: documentID },
      });
      if (res) {
        setEmbedRemoveSuccess(true);
        setLoadingEmbedRemove(false);
      }
    } catch (error) {
      setEmbedRemoveSuccess(false);
      setLoadingEmbedRemove(false);
      console.log(error);
    }
  };
  // three buttons end

  const [documentData, setDocumentData] = useState<Document | null>(null);
  const [pdfCurrentPage, setPdfCurrentPage] = React.useState<number>(0);
  const [highlights, setHighlights] = React.useState<HighlightItem[]>([]);
  const [selectedChunk, setSelectedChunk] = React.useState<string>("");

  useEffect(() => {
    const fetchDocument = async () => {
      const endpoint = `${basePath}/document`;
      const requestBody = {
        id: documentID,
      };
      const token = localStorage.getItem("authToken");

      try {
        const response = await fetch(endpoint, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(requestBody),
          credentials: "include",
        });

        if (!response.ok) {
          console.log("!response.ok");
          throw new Error(`Error: ${response.status}`);
        }

        const data = await response.json();
        console.log("document", data); // Handle the response data as needed
        setDocumentData(data.document);

        // set highlights
        const highlights = data.document.chunks
          ? data.document.chunks
              .filter((chunk: any) => chunk.metadata.coordinates)
              .map((chunk: any) => {
                const coordinates = chunk.metadata.coordinates;
                const selectonRegion = convertToHighlightArea(
                  coordinates,
                  chunk.metadata.page_number // 1
                );
                return {
                  id: chunk.element_id,
                  image: "",
                  selectionRegion: selectonRegion,
                };
              })
          : [];
        console.log("highlights", highlights);
        setHighlights(highlights);
      } catch (error) {
        console.error("Failed to fetch document:", error);
      }
    };

    fetchDocument();
  }, [documentID]);

  const onPageChangeCallback = (e: PageChangeEvent) => {
    console.log("page changed", e);
    setPdfCurrentPage(e.currentPage);
  };

  const renderHighlights = (props: RenderHighlightsProps) => {
    return (
      <div>
        {highlights &&
          highlights.length > 0 &&
          highlights
            .filter(
              (highlight) =>
                highlight.selectionRegion.pageIndex === props.pageIndex
            )
            .map(
              (highlight: {
                id: number;
                selectionRegion: {
                  left: any;
                  top: any;
                  width: any;
                  height: any;
                  pageIndex: number;
                };
              }) => (
                // eslint-disable-next-line
                <a
                  className="hightlight-target"
                  key={highlight.id}
                  style={{
                    border: "1px solid red",
                    position: "absolute",
                    display: "block",
                    left: `${highlight.selectionRegion.left}%`,
                    top: `${highlight.selectionRegion.top}%`,
                    width: `${highlight.selectionRegion.width}%`,
                    minWidth: `${highlight.selectionRegion.width}%`,
                    height: `${highlight.selectionRegion.height}%`,
                    minHeight: `${highlight.selectionRegion.height}%`,
                    zIndex: 1,
                  }}
                  href={`#${highlight.id}`}
                  onClick={() => {
                    setSelectedChunk(`${highlight.id}`);
                  }}
                ></a>
              )
            )}
      </div>
    );
  };

  const highlightPluginInstance = highlightPlugin({
    renderHighlights,
    trigger: Trigger.None,
  });

  interface BoxCoordinates {
    left: number;
    top: number;
    width: number;
    height: number;
  }

  const handleSelection = (selection: {
    image: string;
    selectionRegion: BoxCoordinates;
  }) => {
    const newSelection = {
      id: highlights.length + 1,
      image: selection.image,
      selectionRegion: {
        left: selection.selectionRegion.left,
        top: selection.selectionRegion.top,
        width: selection.selectionRegion.width,
        height: selection.selectionRegion.height,
        pageIndex: pdfCurrentPage,
      },
    };

    const newChunk = selectionToChunk(
      newSelection,
      documentData?.fileName || "",
      documentData?.chunks[0].metadata.filetype || ""
    );

    console.log("newSelection", newChunk);

    const endpoint = `${basePath}/document/chunk/add`;
    const requestBody = {
      documentId: documentData?.id,
      chunkId: newChunk.element_id,
      fieldName: "",
      newValue: newChunk,
    };

    console.log("requestBody", requestBody);
    const token = localStorage.getItem("authToken");

    fetch(endpoint, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        credentials: "include",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(requestBody),
      credentials: "include",
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Error: ${response.status}`);
        }
        return response.json();
      })
      .then((data) => {
        console.log(data);
      })
      .catch((error) => {
        console.error("Failed to add chunk:", error);
      });
  };

  const customRenderPage: RenderPage = (props: RenderPageProps) => {
    return (
      <ClickToSelect onSelectionChange={handleSelection}>
        {props.canvasLayer.children}
        {props.annotationLayer.children}
        {props.textLayer.children}
      </ClickToSelect>
    );
  };

  function onClickEditChunk(documentId: string, chunkId: string) {
    const fieldName = "";
    const newValue = window.prompt("Enter the new value:");

    // get the chunk object
    const chunk = documentData?.chunks.find(
      (chunk) => chunk.element_id === chunkId
    );
    // change to text
    if (chunk && newValue) {
      chunk.text = newValue;
    }

    console.log("chunk", chunk);

    const endpoint = `${basePath}/document/chunk/edit`;
    const requestBody = {
      documentId,
      chunkId,
      fieldName,
      newValue: chunk,
    };
    const token = localStorage.getItem("authToken");

    fetch(endpoint, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        credentials: "include",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(requestBody),
      credentials: "include",
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Error: ${response.status}`);
        }
        return response.json();
      })
      .then((data) => {
        console.log(data);
      })
      .catch((error) => {
        console.error("Failed to edit chunk:", error);
      });
  }

  function onClickDeleteChunk(documentId: string, chunkId: string) {
    const endpoint = `${basePath}/document/chunk/delete`;
    const requestBody = {
      documentId,
      chunkId,
    };
    const token = localStorage.getItem("authToken");

    fetch(endpoint, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        credentials: "include",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(requestBody),
      credentials: "include",
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Error: ${response.status}`);
        }
        return response.json();
      })
      .then((data) => {
        console.log(data);
      })
      .catch((error) => {
        console.error("Failed to delete chunk:", error);
      });
  }
  return (
    <>
      <h1>Admins use this page to annotate pdfs</h1>
      <Box
        display="flex"
        flexDirection="column"
        alignItems="flex-start"
        gap={2}
        marginLeft={2}
      >
        <Box display="flex" alignItems="center" gap={1}>
          <Button
            variant="contained"
            color="primary"
            onClick={() => handleDocumentChunk()}
            disabled={loadingChunk || chunkJobId !== null}
          >
            Create Chunck Job
          </Button>
          {loadingChunk && <CircularProgress size={24} />}
          {chunkMessage !== null && (
            <Box display="flex" alignItems="center" gap={1}>
              {chunkMessage}
            </Box>
          )}
        </Box>
      </Box>
      <Box
        display="flex"
        flexDirection="column"
        alignItems="flex-start"
        gap={2}
        marginLeft={2}
      >
        <Box display="flex" alignItems="center" gap={1}>
          <Button
            variant="contained"
            color="primary"
            onClick={() => checkChunkStatus()}
            disabled={loadingChunkStatus || chunkJobId == null}
          >
            Check Chunck Status
          </Button>
          {loadingChunkStatus && <CircularProgress size={24} />}
          {chunkStatus !== null && (
            <Box display="flex" alignItems="center" gap={1}>
              {chunkStatus}
            </Box>
          )}
        </Box>
      </Box>
      <Box
        display="flex"
        flexDirection="column"
        alignItems="flex-start"
        gap={2}
        marginLeft={2}
        marginTop={3}
        marginBottom={3}
      >
        <Box display="flex" alignItems="center" gap={1}>
          <Button
            variant="contained"
            color="primary"
            onClick={() => handleDocumentDesribe()}
            disabled={loadingDescribe || describeSuccess === true}
          >
            Describe document
          </Button>
          {loadingDescribe && <CircularProgress size={24} />}
          {describeSuccess !== null && (
            <Box display="flex" alignItems="center" gap={1}>
              {describeSuccess ? (
                <>
                  <CheckCircleIcon color="success" />
                  <Typography variant="body1">
                    Successfully Described!
                  </Typography>
                </>
              ) : (
                <>
                  <ErrorIcon color="error" />
                  <Typography variant="body1">Describe failed!</Typography>
                </>
              )}
            </Box>
          )}
        </Box>
      </Box>

      <Box
        display="flex"
        flexDirection="column"
        alignItems="flex-start"
        gap={2}
        marginLeft={2}
        marginBottom={3}
      >
        <Box display="flex" alignItems="center" gap={1}>
          <Button
            variant="contained"
            color="primary"
            onClick={() => handleDocumentEmbed()}
            disabled={loadingEmbed || embedSuccess === true}
          >
            Embed document
          </Button>
          {loadingEmbed && <CircularProgress size={24} />}
          {embedSuccess !== null && (
            <Box display="flex" alignItems="center" gap={1}>
              {embedSuccess ? (
                <>
                  <CheckCircleIcon color="success" />
                  <Typography variant="body1">Successfully Embeded!</Typography>
                </>
              ) : (
                <>
                  <ErrorIcon color="error" />
                  <Typography variant="body1">Embed failed!</Typography>
                </>
              )}
            </Box>
          )}
        </Box>
        <Box display="flex" alignItems="center" gap={1}>
          <Button
            variant="contained"
            color="error"
            onClick={() => handleDocumentEmbedRemove()}
          >
            Remove Embedding
          </Button>
        </Box>
        {loadingEmbedRemove && <CircularProgress size={24} />}
        {embedRemoveSuccess !== null && (
          <Box display="flex" alignItems="center" gap={1}>
            {embedRemoveSuccess ? (
              <>
                <CheckCircleIcon color="success" />
                <Typography variant="body1">
                  Successfully removed embeddings!
                </Typography>
              </>
            ) : (
              <>
                <ErrorIcon color="error" />
                <Typography variant="body1">Embed remove failed!</Typography>
              </>
            )}
          </Box>
        )}
      </Box>
      {documentData && (
        <PDFViewer
          pdfFile={documentData.documentContent}
          onPageChangeCallback={onPageChangeCallback}
          highlightPlugin={highlightPluginInstance}
          // previousHighlights={}
          highlights={highlights}
          customRenderPage={customRenderPage}
        />
      )}
      <div>
        {documentData && (
          <>
            <h2>Document Information</h2>
            <table className="table">
              <tbody>
                <tr>
                  <th>ID</th>
                  <td>{documentData.id}</td>
                </tr>
                <tr>
                  <th>File Name</th>
                  <td>{documentData.fileName}</td>
                </tr>
              </tbody>
            </table>

            <h3>Chunks</h3>
            <table className="table">
              <thead>
                <tr>
                  <th>text</th>
                  <th>type</th>
                  <th>page_number</th>
                  <th>element_id</th>
                  <th>customize</th>
                </tr>
              </thead>
              <tbody>
                {documentData.chunks &&
                  documentData.chunks.map((chunk, index) => (
                    <tr
                      key={index}
                      id={chunk.element_id}
                      style={
                        selectedChunk === chunk.element_id
                          ? { backgroundColor: "red" }
                          : {}
                      }
                    >
                      <td>{chunk.text}</td>
                      <td>{chunk.type}</td>
                      <td>{JSON.stringify(chunk.metadata.page_number)}</td>
                      <td>{chunk.element_id}</td>
                      <td>
                        <button
                          style={{
                            backgroundColor: "yellow",
                            marginRight: "5px",
                          }}
                          onClick={() =>
                            onClickEditChunk(documentData.id, chunk.element_id)
                          }
                        >
                          Edit Chunk
                        </button>
                        <button
                          style={{ backgroundColor: "red" }}
                          onClick={() =>
                            onClickDeleteChunk(
                              documentData.id,
                              chunk.element_id
                            )
                          }
                        >
                          Delete Chunk
                        </button>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </>
        )}
      </div>
    </>
  );
};
